import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { memoriesApi, AgentMemory } from '@/services/api/agentApi';

interface FieldTemplate {
  key: string;
  label: string;
  type: 'color' | 'text' | 'number';
  placeholder?: string;
}

interface DirectiveSectionProps {
  title: string;
  description: string;
  aspectFilter: string;
  template?: FieldTemplate[];
  freeForm?: boolean;
}

export const DirectiveSection: React.FC<DirectiveSectionProps> = ({
  title,
  description,
  aspectFilter,
  template,
  freeForm,
}) => {
  const [memories, setMemories] = useState<AgentMemory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newRuleText, setNewRuleText] = useState('');

  const fetchDirectives = useCallback(async () => {
    try {
      setLoading(true);
      const result = await memoriesApi.byAspects({ aspects: aspectFilter, type: 'directive' });
      setMemories(result.data || []);
    } catch {
      // Silently handle — empty state is fine
      setMemories([]);
    } finally {
      setLoading(false);
    }
  }, [aspectFilter]);

  useEffect(() => {
    fetchDirectives();
  }, [fetchDirectives]);

  const handleSave = async (memory: AgentMemory) => {
    const draftText = drafts[memory.memory_id];
    if (!draftText || draftText === memory.text) return;

    setSaving(memory.memory_id);
    try {
      await memoriesApi.update(memory.memory_id, { text: draftText });
      await fetchDirectives();
      setDrafts(prev => {
        const next = { ...prev };
        delete next[memory.memory_id];
        return next;
      });
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (memoryId: string) => {
    setSaving(memoryId);
    try {
      await memoriesApi.delete(memoryId);
      await fetchDirectives();
    } finally {
      setSaving(null);
    }
  };

  const handleCreate = async (text: string) => {
    if (!text.trim()) return;
    setSaving('new');
    try {
      await memoriesApi.create({
        text: text.trim(),
        type: 'directive',
        weight: 1.0,
        aspects: [aspectFilter],
      } as Partial<AgentMemory>);
      setNewRuleText('');
      await fetchDirectives();
    } finally {
      setSaving(null);
    }
  };

  const handleTemplateCreate = async (fields: Record<string, string>) => {
    const text = Object.entries(fields)
      .filter(([, v]) => v.trim())
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n');
    if (!text) return;
    await handleCreate(text);
  };

  if (loading) {
    return (
      <div className="border border-border rounded-lg p-4">
        <h3 className="font-semibold text-base mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground mb-3">{description}</p>
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg p-4">
      <h3 className="font-semibold text-base mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground mb-3">{description}</p>

      {/* Existing directives */}
      <div className="space-y-2 mb-3">
        {memories.map(m => (
          <div key={m.memory_id} className="flex items-start gap-2 bg-muted/50 rounded-md p-2">
            <Textarea
              className="flex-1 min-h-[60px] text-sm"
              value={drafts[m.memory_id] ?? m.text}
              onChange={e => setDrafts(prev => ({ ...prev, [m.memory_id]: e.target.value }))}
            />
            <div className="flex flex-col gap-1">
              {drafts[m.memory_id] && drafts[m.memory_id] !== m.text && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleSave(m)}
                  disabled={saving === m.memory_id}
                >
                  {saving === m.memory_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => handleDelete(m.memory_id)}
                disabled={saving === m.memory_id}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Free-form add */}
      {freeForm && (
        <div className="flex items-start gap-2">
          <Textarea
            className="flex-1 min-h-[60px] text-sm"
            placeholder="Enter a design rule (e.g. 'Always use rounded corners with 12px radius')"
            value={newRuleText}
            onChange={e => setNewRuleText(e.target.value)}
          />
          <Button
            size="icon"
            variant="outline"
            onClick={() => handleCreate(newRuleText)}
            disabled={saving === 'new' || !newRuleText.trim()}
          >
            {saving === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
      )}

      {/* Template-based add */}
      {template && !freeForm && (
        <TemplateForm
          template={template}
          saving={saving === 'new'}
          onSubmit={handleTemplateCreate}
        />
      )}
    </div>
  );
};

function TemplateForm({
  template,
  saving,
  onSubmit,
}: {
  template: FieldTemplate[];
  saving: boolean;
  onSubmit: (fields: Record<string, string>) => void;
}) {
  const [fields, setFields] = useState<Record<string, string>>({});

  const handleChange = (key: string, value: string) => {
    setFields(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    onSubmit(fields);
    setFields({});
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {template.map(t => (
          <div key={t.key}>
            <label className="text-xs font-medium text-muted-foreground">{t.label}</label>
            <Input
              type={t.type === 'color' ? 'color' : t.type === 'number' ? 'number' : 'text'}
              placeholder={t.placeholder}
              value={fields[t.key] || ''}
              onChange={e => handleChange(t.key, e.target.value)}
              className={t.type === 'color' ? 'h-10 w-20 p-1' : 'text-sm'}
            />
          </div>
        ))}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleSubmit}
        disabled={saving || !Object.values(fields).some(v => v.trim())}
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
        Add Directive
      </Button>
    </div>
  );
}
